import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import SEO, { DEFAULT_IMAGE, SITE_URL } from './components/SEO/SEO';
import TextEncodingRepair from './components/TextEncodingRepair/TextEncodingRepair';
import { CartProvider } from './context/CartContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import './app.css';
import store from './data/store.json';
import LoadingState from './components/LoadingState/LoadingState';
import storefrontCopy from './data/storefrontCopy.json';

const Shop = lazy(() => import('./pages/Shop/Shop'));
const AboutUs = lazy(() => import('./pages/AboutUs/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs/ContactUs'));
const BuyPage = lazy(() => import('./pages/BuyPage'));
const ProductDetail = lazy(() => import('./pages/ProductDetail/ProductDetail'));
const Assistant = lazy(() => import('./components/Assistant/Assistant'));
const Landing = lazy(() => import('./pages/Landing/Landing'));

const baseKeywords = [
  'iphone occasion fes',
  'telephone occasion fes',
  'airpods original maroc',
  'iphone maroc',
  'smartphone occasion maroc',
  'mobiluxe store',
  'telephone fes',
  'livraison telephone maroc',
];

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Mobiluxe Store',
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo.png`,
  sameAs: ['https://www.instagram.com/mobiluxe_store/'],
  contactPoint: [{
    '@type': 'ContactPoint',
    telephone: '+212784814047',
    contactType: 'customer service',
    areaServed: 'MA',
    availableLanguage: ['fr', 'ar'],
  }],
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'ElectronicsStore',
  '@id': `${SITE_URL}/#store`,
  name: 'Mobiluxe Store',
  image: DEFAULT_IMAGE,
  url: SITE_URL,
  telephone: '+212784814047',
  areaServed: { '@type': 'Country', name: 'Morocco' },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: store.days,
    opens: store.opens,
    closes: store.closes,
  },
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Kissariat Nour Saada, en face Rayhana',
    addressLocality: 'Fes',
    addressCountry: 'MA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 34.022768,
    longitude: -5.010494,
  },
  sameAs: ['https://www.instagram.com/mobiluxe_store/'],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Mobiluxe Store',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/shop?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

function LegacyHomeRedirect() {
  const location = useLocation();

  return <Navigate to={`/${location.search}${location.hash}`} replace />;
}

function DeferredAssistant() {
  const location = useLocation();
  const isProductPage = location.pathname.startsWith('/produit/');
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const activate = () => setShouldRender(true);

    if (typeof window === 'undefined') {
      activate();
      return undefined;
    }

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(activate, { timeout: 1500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(activate, 800);
    return () => window.clearTimeout(timeoutId);
  }, []);

  if (!isProductPage && !shouldRender) return null;

  return (
    <Suspense fallback={null}>
      <Assistant />
    </Suspense>
  );
}

function AppContent() {
  const { t, language } = useLanguage();
  useEffect(() => { document.querySelector('link[href="/seo-pages.css"]')?.remove(); }, []);
  return (
    <div className="App">
      <Header />
      <main className="app-main">
        <Suspense fallback={<LoadingState />}>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <SEO
                    title={t('seo.homeTitle')}
                    description="Mobiluxe Store à Saada, Fès : iPhone, AirPods, Samsung Buds, montres connectées, chargeurs et accessoires. Livraison à Meknès et partout au Maroc."
                    keywords={baseKeywords}
                    canonicalPath="/"
                    structuredData={[organizationSchema, localBusinessSchema, websiteSchema]}
                  />
                  <Home />
                </>
              }
            />
            <Route
              path="/home"
              element={
                <LegacyHomeRedirect />
              }
            />
            <Route
              path="/shop"
              element={
                <>
                  <SEO
                    title={t('seo.shopTitle')}
                    description="iPhone, AirPods, Samsung Buds, Apple Watch, Kieslect, chargeurs Ugreen, accessoires Oraimo et traceurs GPS à Fès. Livraison partout au Maroc."
                    keywords={[
                      ...baseKeywords,
                      'boutique telephone maroc',
                      'redmi occasion maroc',
                      'ecouteurs bluetooth maroc',
                    ]}
                    canonicalPath="/shop"
                    structuredData={[
                      organizationSchema,
                      breadcrumbSchema([
                        { name: 'Accueil', path: '/' },
                        { name: 'Boutique', path: '/shop' },
                      ]),
                    ]}
                  />
                  <Shop />
                </>
              }
            />
            <Route
              path="/about-us"
              element={
                <>
                  <SEO
                    title={t('seo.aboutTitle')}
                    description={storefrontCopy[language].about.intro}
                    keywords={[...baseKeywords, 'magasin telephone fes', 'mobiluxe fes']}
                    canonicalPath="/about-us"
                    structuredData={[
                      organizationSchema,
                      breadcrumbSchema([
                        { name: 'Accueil', path: '/' },
                        { name: 'A propos', path: '/about-us' },
                      ]),
                    ]}
                  />
                  <AboutUs />
                </>
              }
            />
            <Route
              path="/contact-us"
              element={
                <>
                  <SEO
                    title={t('seo.contactTitle')}
                    description="Contactez Mobiluxe Store à Fès : téléphone 0784 81 40 47, WhatsApp, adresse Kissariat Nour Saada en face Rayhana et livraison au Maroc."
                    keywords={[...baseKeywords, 'contact mobiluxe', 'magasin telephone fes', 'kissariat nour saada']}
                    canonicalPath="/contact-us"
                    structuredData={[
                      localBusinessSchema,
                      breadcrumbSchema([
                        { name: 'Accueil', path: '/' },
                        { name: 'Contact', path: '/contact-us' },
                      ]),
                    ]}
                  />
                  <ContactUs />
                </>
              }
            />
            <Route
              path="/buy"
              element={
                <>
                  <SEO
                    title={t('seo.checkoutTitle')}
                    description="Finalisez votre commande Mobiluxe Store en toute sécurité."
                    canonicalPath="/buy"
                    robots="noindex,nofollow"
                  />
                  <BuyPage />
                </>
              }
            />
            <Route
              path="/produit/:categorySlug/:subcategorySlug/:productSlug"
              element={<ProductDetail />}
            />
            <Route path="/produit/:productId" element={<ProductDetail />} />
            <Route path="/magasin-fes" element={<Landing />} />
            <Route path="/magasin-fes/index.html" element={<Landing />} />
            <Route path="/livraison-maroc" element={<Landing />} />
            <Route path="/livraison-maroc/index.html" element={<Landing />} />
            <Route path="/collections/:slug" element={<Landing />} />
            <Route path="/collections/:slug/index.html" element={<Landing />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <DeferredAssistant />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <CartProvider>
        <Router>
          <TextEncodingRepair />
          <AppContent />
        </Router>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
